import { createHash } from "crypto";
//#region src/setup-marker.ts
/** Directory, relative to the template's build cwd, that holds every marker below. */
const SETUP_MARKER_DIR = ".mastra-sandbox";
/**
* Setup completion marker shared by repo templates and their consumers.
*
* A repo template writes this file beside the checkout as its last build
* step, so it exists only in images where every setup command succeeded. Its
* content is a digest of the setup commands the image ran, letting a sandbox
* booted from the image tell whether the setup it is about to run already
* happened. Relative to the template's build cwd, which is also the runtime
* working directory the repo was cloned into.
*
* This is the single-repository marker. Templates built from a repository
* list write one marker per repository instead ({@link repoSetupMarkerPath}).
*/
const SETUP_MARKER_PATH = ".mastra-sandbox/setup";
/**
* Marker for the workspace-level setup of a multi-repository template: the
* commands that ran at the working directory after every repository was set
* up. Written even when there were no such commands, so a consumer can tell
* "ran nothing" from "never built".
*/
const WORKSPACE_SETUP_MARKER_PATH = ".mastra-sandbox/workspace-setup";
/**
* One line per repository directory whose setup failed while the build was
* told to continue. Absent when nothing failed. Consumers check this before
* trusting a per-repository marker: that marker means every step ran, not
* that every step succeeded.
*/
const SETUP_FAILED_MARKER_PATH = ".mastra-sandbox/setup-failed";
/** Per-repository marker of a multi-repository template, keyed by the clone directory name. */
function repoSetupMarkerPath(repoDir) {
	return `${SETUP_MARKER_DIR}/repos/${repoDir}`;
}
/** Blank entries never become build steps, so they never count toward the digest either. */
function normalizeSetupCommands(setupCommand) {
	return (setupCommand === void 0 ? [] : Array.isArray(setupCommand) ? setupCommand : [setupCommand]).filter((command) => command.trim() !== "");
}
/** The marker content for a setup command list: `sha256:<hex>` over the commands joined by newlines. */
function setupMarkerContent(setupCommand) {
	return `sha256:${createHash("sha256").update(normalizeSetupCommands(setupCommand).join("\n")).digest("hex")}`;
}
/** Shell step that writes the marker relative to the cwd. `content` is a digest, so it is shell-safe. */
function setupMarkerCommand(content, markerPath = SETUP_MARKER_PATH) {
	return `mkdir -p "$(dirname "${markerPath}")" && printf '%s' '${content}' > "${markerPath}"`;
}
/** A clone directory name is one path segment of word characters, dots and dashes, so it never needs quoting. */
const REPO_DIR_PATTERN = /^[\w.-]+$/;
/**
* Shell step that runs one setup command inside a repository clone.
*
* Without the guard this is the plain `cd "<dir>" && <cmd>` step. With it,
* the `cd` sits inside the parenthesized group so that the failure branch
* runs back at the build cwd and the failure list lands at the workspace
* level, never inside the repository.
*/
function guardedSetupCommand({ repoDir, command, continueOnFailure }) {
	if (!REPO_DIR_PATTERN.test(repoDir)) throw new TypeError(`Repository directory "${repoDir}" must match ${REPO_DIR_PATTERN}.`);
	if (!continueOnFailure) return `cd "${repoDir}" && ${command}`;
	return `( cd "${repoDir}" && sh -c ${shellQuote$1(command)} ) || { mkdir -p "${SETUP_MARKER_DIR}" && grep -qxF -- '${repoDir}' "${SETUP_FAILED_MARKER_PATH}" 2>/dev/null || printf '%s\\n' '${repoDir}' >> "${SETUP_FAILED_MARKER_PATH}"; }`;
}
function shellQuote$1(value) {
	return `'${value.replace(/'/g, `'\\''`)}'`;
}
//#endregion
//#region src/repo-clone.ts
function repoCloneCommand({ cloneUrl, destination, branch, tokenEnv }) {
	return `git ${tokenEnv ? `${gitAuthFlag(tokenEnv)} ` : ""}clone --depth=1 --single-branch ${branch ? `--branch ${shellQuote(branch)} ` : ""}${shellQuote(cloneUrl)} ${shellQuote(destination)}`;
}
/** Per-invocation auth header; `-c` config never reaches `.git/config`. */
function gitAuthFlag(tokenEnv) {
	return `-c http.extraheader="AUTHORIZATION: basic $(printf 'x-access-token:%s' "$${tokenEnv}" | base64 -w0)"`;
}
function shellQuote(value) {
	return `'${value.replace(/'/g, `'\\''`)}'`;
}
//#endregion
export { SETUP_FAILED_MARKER_PATH, SETUP_MARKER_DIR, SETUP_MARKER_PATH, WORKSPACE_SETUP_MARKER_PATH, guardedSetupCommand, normalizeSetupCommands, repoCloneCommand, repoSetupMarkerPath, setupMarkerCommand, setupMarkerContent };

//# sourceMappingURL=index.js.map